import React, { useState, useRef, useEffect } from "react";
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  QrCodeIcon,
  SparklesIcon,
  BuildingOfficeIcon,
  CubeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import { request } from "config/request";
import UseToast from "utils/AfToast";
import { soundFX } from "../soundEffects";
import { useAuthStore } from "store/auth";
import Select from "react-select";
import { ListCommanWay } from "API/commonApi";
import { useQuery } from "@tanstack/react-query";
import { BoxStatusBadge } from "../BoxTracker";

const compactSelectStyles = {
  control: (base: any, state: any) => ({
    ...base,
    background: "white",
    borderRadius: "10px",
    borderColor: state.isFocused ? "#0077B6" : "#cbd5e1",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(0, 119, 182, 0.15)" : "none",
    minHeight: "38px",
    fontSize: "12px",
  }),
  valueContainer: (base: any) => ({ ...base, padding: "0 10px" }),
  menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
};

export const ReturnCheckInTab: React.FC<{
  onOpenTimeline: (qrCode: string) => void;
}> = ({ onOpenTimeline }) => {
  const user = useAuthStore((s) => s.user);
  const [qrInput, setQrInput] = useState("");
  const [selectedSubbranch, setSelectedSubbranch] = useState<any>(null);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastReturnedBox, setLastReturnedBox] = useState<any>(null);
  const [sessionReturns, setSessionReturns] = useState<any[]>([]);
  const [errorMsg, setErrorMsg] = useState("");

  const inputRef = useRef<HTMLInputElement>(null);

  const { data: subbranchesData } = useQuery(["SubBranchesForReturnCheckIn"], () =>
    ListCommanWay("/admin/subbranch/list", {}, { paginate: 1000 })
  );

  const subbranchOptions = (subbranchesData?.data?.data || []).map((sb: any) => ({
    value: sb.id,
    label: `${sb.branch_name || sb.name || "Hub"} ${sb.code ? `(${sb.code})` : ""}`,
  }));

  useEffect(() => {
    if (subbranchOptions.length > 0 && !selectedSubbranch) {
      const userSubbranch = (user as any)?.subbranch;
      if (userSubbranch) {
        const found = subbranchOptions.find((o: any) => o.value === userSubbranch);
        if (found) setSelectedSubbranch(found);
      } else if (subbranchOptions[0]) {
        setSelectedSubbranch(subbranchOptions[0]);
      }
    }
  }, [subbranchOptions, user]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleScanSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const code = qrInput.trim().toUpperCase();
    if (!code) return;

    setErrorMsg("");

    // Detect if user accidentally scanned an AWB instead of a Box QR
    if (/^[a-zA-Z]\d+$/.test(code)) {
      soundFX.playError();
      const err = `This looks like an AWB number (${code})! Return check-in is for Multi-Use Box QRs (e.g. 123123, BX-123123, AHD123123).`;
      setErrorMsg(err);
      UseToast(err, "error");
      return;
    }

    setLoading(true);

    try {
      const res: any = await request({
        url: "/admin/box-tracker/checkin-returned",
        method: "POST",
        body: {
          qrCode: code,
          subbranch: selectedSubbranch?.value || undefined,
          notes: notes.trim() || "Returned in good condition",
        },
      });

      if (res?.status === "SUCCESS" && res.data?.box) {
        soundFX.playSuccess();
        const returned = res.data.box;
        setLastReturnedBox(returned);
        UseToast(
          res.message || `Multi-use box '${returned.qrCode}' successfully returned to stock! Trip #${returned.usageCount}`,
          "success"
        );

        setSessionReturns((prev) => [
          {
            box: returned,
            scannedAt: new Date().toLocaleTimeString(),
            notes: notes.trim(),
          },
          ...prev,
        ]);

        setQrInput("");
        setNotes("");
        inputRef.current?.focus();
      } else {
        soundFX.playError();
        setErrorMsg(res?.message || "Failed to check in returned box.");
      }
    } catch (err: any) {
      soundFX.playError();
      setErrorMsg(typeof err === "string" ? err : err?.message || "Failed to check in box.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-4">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <QrCodeIcon className="w-4 h-4 text-teal-600" />
                <span>Handheld Return Scanner</span>
              </h3>
              <span className="text-[11px] text-slate-400">Scanner ready</span>
            </div>

            {errorMsg && (
              <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-medium flex items-center gap-2">
                <ExclamationTriangleIcon className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{errorMsg}</span>
              </div>
            )}

            <form onSubmit={handleScanSubmit} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1 flex items-center gap-1.5">
                  <BuildingOfficeIcon className="w-3.5 h-3.5 text-slate-400" />
                  <span>Receiving Hub / Subbranch</span>
                </label>
                <Select
                  options={subbranchOptions}
                  value={selectedSubbranch}
                  onChange={setSelectedSubbranch}
                  styles={compactSelectStyles}
                  placeholder="Select receiving hub..."
                  menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                />
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-teal-900 mb-1 flex items-center justify-between">
                  <span>Scanned Box QR Code *</span>
                  <span className="text-[10px] text-slate-400 font-normal">Press Enter</span>
                </label>
                <div className="relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={qrInput}
                    onChange={(e) => setQrInput(e.target.value.toUpperCase())}
                    placeholder="Scan Multi-Use Box QR (e.g. 123123, BX-123123, AHD123123)..."
                    className="w-full pl-10 pr-24 py-3.5 text-base font-mono font-bold border-2 border-teal-300 rounded-xl focus:outline-none focus:border-teal-600 bg-teal-50/30 focus:bg-white uppercase"
                    autoFocus
                    required
                  />
                  <QrCodeIcon className="w-5 h-5 text-teal-600 absolute left-3 top-4" />
                  <button
                    type="submit"
                    disabled={loading || !qrInput.trim()}
                    className="absolute right-2 top-2 px-3.5 py-2 bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold rounded-lg disabled:opacity-40"
                  >
                    {loading ? "..." : "Check In"}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-700 mb-1">
                  Condition / Cleaning Notes (optional)
                </label>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g. Clean condition, gel packs removed..."
                  className="w-full px-3 py-2 text-xs border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-600"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !qrInput.trim()}
                className="w-full py-3 px-4 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-700 hover:to-emerald-700 text-white rounded-xl font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2 disabled:opacity-40"
              >
                <ArrowPathIcon className="w-4 h-4" />
                <span>Return to Active Stock</span>
              </button>
            </form>
          </div>

          {lastReturnedBox && (
            <div className="bg-gradient-to-br from-teal-50 via-emerald-50 to-white rounded-2xl border-2 border-emerald-300 p-5 shadow-sm space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-emerald-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-900">
                    Returned to Stock!
                  </span>
                </div>
                <BoxStatusBadge status={lastReturnedBox.status || "IN_STOCK"} size="sm" />
              </div>

              <div className="flex items-baseline justify-between pt-1">
                <div>
                  <span className="font-mono text-xl font-black text-slate-900 block">
                    {lastReturnedBox.qrCode}
                  </span>
                  <span className="text-xs text-slate-500">
                    Box ID #{lastReturnedBox.id} &bull; Type: {lastReturnedBox.boxType}
                  </span>
                </div>
                <span className="bg-teal-600 text-white px-3 py-1 rounded-full text-xs font-black">
                  Trip #{lastReturnedBox.usageCount} Completed
                </span>
              </div>

              <div className="pt-2 border-t border-emerald-200/60 flex items-center justify-between text-xs text-slate-600">
                <span>Hub ID: #{lastReturnedBox.currentSubbranch || "—"}</span>
                <button
                  type="button"
                  onClick={() => onOpenTimeline(lastReturnedBox.qrCode)}
                  className="font-semibold text-teal-700 hover:underline"
                >
                  View Box Journey &rarr;
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-5 space-y-3">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 pb-2 border-b border-slate-100 flex items-center gap-1.5">
              <CubeIcon className="w-4 h-4 text-teal-600" />
              <span>Returned Multi-Use Boxes This Shift ({sessionReturns.length})</span>
            </h3>

            {sessionReturns.length === 0 ? (
              <div className="py-16 text-center text-slate-400 text-xs border border-dashed rounded-xl">
                No boxes checked in yet. Scan returned boxes on the left.
              </div>
            ) : (
              <div className="space-y-2.5 max-h-[500px] overflow-y-auto pr-1">
                {sessionReturns.map((item, idx) => (
                  <div key={idx} className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex flex-col gap-1.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-slate-900 text-sm">{item.box.qrCode}</span>
                        <span className="bg-teal-100 text-teal-800 text-[10px] font-bold px-2 py-0.5 rounded-full">
                          Trip #{item.box.usageCount}
                        </span>
                      </div>
                      <span className="text-[10px] text-slate-400">{item.scannedAt}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500 pt-1 border-t border-slate-100">
                      <span className="truncate">{item.notes || "Clean condition"}</span>
                      <button type="button" onClick={() => onOpenTimeline(item.box.qrCode)} className="text-[11px] font-semibold text-teal-700 hover:underline">
                        Timeline &rarr;
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
